// ─────────────────────────────────────────────────────────────────────────────
// C7 — Pivota las celdas planas del mapa de calor (OU × Fundamental) a los ejes que pinta
// `HeatmapTab` y que también alimenta el export consolidado del panel Demográfico
// (`DemograficoExportButton`). Extraído de `HeatmapTab` para que las dos superficies compartan
// una sola implementación del pivote — antes vivía solo en un `useMemo` del componente.
// ─────────────────────────────────────────────────────────────────────────────
import type { HeatmapCellDto } from '@/api/recognition';

export interface HeatmapAxis {
  id: string;
  name: string;
}

/**
 * Punto 2 (revisión final, 2026-09-03) — recorta las celdas al Fundamental elegido en la tira.
 *
 * Compartida entre `HeatmapTab` (pantalla) y `DemograficoExportButton` (`buildHeatmapSheet`): antes
 * cada uno tenía su propio recorte y el export se olvidó de aplicarlo — con un Fundamental puesto la
 * pantalla mostraba una columna y el Excel descargado las mostraba todas. El recorte se hace EN
 * CLIENTE y no pidiendo un parámetro nuevo al backend: la matriz ya llega como celdas (OU ×
 * Fundamental) con `Score` normalizado POR COLUMNA, así que quedarse con una sola columna no
 * distorsiona ningún valor.
 */
export function celdasFiltradasPorFundamental(
  cells: readonly HeatmapCellDto[],
  fundamentalId: string | undefined,
): readonly HeatmapCellDto[] {
  if (!fundamentalId) return cells;
  return cells.filter((celda) => celda.fundamentalId === fundamentalId);
}

export interface HeatmapMatrix {
  /** Filas — OUs, en orden de primera aparición en `cells`. */
  ous: HeatmapAxis[];
  /** Columnas — Fundamentales: orden del catálogo (`fundamentalOrder`) si se recibe, si no orden de
   *  primera aparición en `cells`. */
  funds: HeatmapAxis[];
  /** Celda por clave `${ouId}::${fundamentalId}`. */
  lookup: Map<string, HeatmapCellDto>;
}

/**
 * Deriva los ejes (OUs = filas, Fundamentales = columnas) y el lookup de celdas a partir del array
 * plano que devuelve `GET v1/analytics/recognition/heatmap`. AnalyticsService todavía no resuelve
 * `ouName` — se ignora el valor de la API y se resuelve en el front (`resolveOuName`), igual que el
 * resto de AnalyticsView.
 */
export function buildHeatmapMatrix(
  cells: readonly HeatmapCellDto[],
  resolveOuName: (id: string) => string,
  fundamentalOrder?: ReadonlyMap<string, number>,
): HeatmapMatrix {
  const ouMap = new Map<string, HeatmapAxis>();
  const fundMap = new Map<string, HeatmapAxis>();
  const lookup = new Map<string, HeatmapCellDto>();

  for (const cell of cells) {
    if (!ouMap.has(cell.ouId)) ouMap.set(cell.ouId, { id: cell.ouId, name: resolveOuName(cell.ouId) });
    if (!fundMap.has(cell.fundamentalId)) {
      fundMap.set(cell.fundamentalId, { id: cell.fundamentalId, name: cell.fundamentalName });
    }
    lookup.set(`${cell.ouId}::${cell.fundamentalId}`, cell);
  }

  // Mismo criterio que Brecha Generacional × Fundamental: el orden visible es el del catálogo
  // (`FundamentalDto.order`), no el de aparición en la respuesta — dos tablas del mismo panel
  // mostrando el mismo tipo de columna no pueden ordenarla distinto.
  const funds = fundamentalOrder
    ? [...fundMap.values()].sort(
        (a, b) => (fundamentalOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER)
          - (fundamentalOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER),
      )
    : [...fundMap.values()];

  return { ous: [...ouMap.values()], funds, lookup };
}

/**
 * Suma de las celdas VISIBLES de una fila (misma regla que la columna «Total» de `HeatmapTab`): las
 * celdas suprimidas por anonimato no se suman como cero —no se conoce su valor—, y `hayTapadas`
 * marca cuándo la suma es un MÍNIMO, no el total real (afirmar «12» cuando hay celdas ocultas sería
 * afirmar un total que no se puede afirmar).
 */
export function sumaFilaVisible(
  ouId: string,
  funds: readonly HeatmapAxis[],
  lookup: ReadonlyMap<string, HeatmapCellDto>,
): { suma: number; hayTapadas: boolean } {
  const visibles = funds
    .map((fund) => lookup.get(`${ouId}::${fund.id}`))
    .filter((cell): cell is HeatmapCellDto => !!cell && !cell.suprimido);

  return {
    suma: visibles.reduce((acc, cell) => acc + cell.count, 0),
    hayTapadas: visibles.length < funds.length,
  };
}

/** Misma regla que `sumaFilaVisible`, pivotada por COLUMNA — para la fila «Total» al pie de la matriz. */
export function sumaColumnaVisible(
  fundId: string,
  ous: readonly HeatmapAxis[],
  lookup: ReadonlyMap<string, HeatmapCellDto>,
): { suma: number; hayTapadas: boolean } {
  const visibles = ous
    .map((ou) => lookup.get(`${ou.id}::${fundId}`))
    .filter((cell): cell is HeatmapCellDto => !!cell && !cell.suprimido);

  return {
    suma: visibles.reduce((acc, cell) => acc + cell.count, 0),
    hayTapadas: visibles.length < ous.length,
  };
}

/**
 * Máximo `count` visible dentro de una columna (Fundamental) — base para la intensidad relativa del
 * color de cada celda: «la intensidad del color muestra la concentración relativa dentro de cada
 * columna», no un score absoluto. Nunca 0 (evita dividir entre cero con una columna vacía).
 */
export function maximoDeColumna(
  fundId: string,
  ous: readonly HeatmapAxis[],
  lookup: ReadonlyMap<string, HeatmapCellDto>,
): number {
  const conteos = ous
    .map((ou) => lookup.get(`${ou.id}::${fundId}`))
    .filter((cell): cell is HeatmapCellDto => !!cell && !cell.suprimido)
    .map((cell) => cell.count);
  return Math.max(1, ...conteos);
}
